import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';
import {
  UserNotification,
  NotificationPreferences,
} from '../types/user';
import { NotificationService } from '../services/notificationService';

// ==================== TYPES ====================

interface NotificationContextType {
  // State
  notifications: UserNotification[];
  unreadCount: number;
  loading: boolean;
  preferences: NotificationPreferences | null;

  // Actions
  refresh: () => Promise<void>;
  markAsRead: (id: string) => Promise<void>;
  markAsUnread: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (id: string) => Promise<void>;
  archiveNotification: (id: string) => Promise<void>;
  unarchiveNotification: (id: string) => Promise<void>;

  // Preferences
  updatePreferences: (updates: Partial<NotificationPreferences>) => Promise<void>;
  setEnabled: (enabled: boolean) => Promise<void>;
  pauseNotifications: (hours: number) => Promise<void>;
}

// ==================== CONTEXT ====================

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined
);

// ==================== PROVIDER ====================

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { authState } = useAuth();
  const user = authState.user;

  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(
    null
  );

  // Real-time listener for notifications
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setPreferences(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    // Set up real-time listener for ALL notifications (read and unread)
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc'),
      limit(100) // Increased limit to show more notifications
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifs = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            userId: data.userId,
            type: data.type,
            title: data.title,
            message: data.message,
            priority: data.priority,
            actionUrl: data.actionUrl,
            actionLabel: data.actionLabel,
            dismissible: data.dismissible ?? true,
            data: data.data || {},
            groupKey: data.groupKey,
            read: data.read ?? false,
            readAt: data.readAt?.toDate(),
            archived: data.archived ?? false,
            archivedAt: data.archivedAt?.toDate(),
            createdBy: data.createdBy || 'system',
            source: data.source || 'automated',
            createdAt: data.createdAt?.toDate() || new Date(),
          } as UserNotification;
        });

        setNotifications(notifs);
        // Calculate unread count from the notifications
        setUnreadCount(notifs.filter((n) => !n.read && !n.archived).length);
        setLoading(false);
      },
      (error) => {
        console.error('Error listening to notifications:', error);
        setLoading(false);
      }
    );

    // Load preferences
    loadPreferences();

    return () => {
      unsubscribe();
    };
  }, [user]);

  // ==================== ACTIONS ====================

  const loadPreferences = async () => {
    if (!user) return;

    try {
      const prefs = await NotificationService.getPreferences(user.uid);
      setPreferences(prefs);
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
  };

  const refresh = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const notifs = await NotificationService.getUserNotifications(user.uid, {
        unreadOnly: true,
        limit: 50,
      });
      setNotifications(notifs);
      setUnreadCount(notifs.length);
    } catch (error) {
      console.error('Error refreshing notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await NotificationService.markAsRead(id);
      // Real-time listener will update state automatically
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAsUnread = async (id: string) => {
    try {
      await NotificationService.markAsUnread(id);
      // Real-time listener will update state automatically
    } catch (error) {
      console.error('Error marking notification as unread:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;

    try {
      await NotificationService.markAllAsRead(user.uid);
      // Real-time listener will update state automatically
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await NotificationService.delete(id);
      // Real-time listener will update state automatically
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const archiveNotification = async (id: string) => {
    try {
      await NotificationService.archive(id);
      // Real-time listener will update state automatically
    } catch (error) {
      console.error('Error archiving notification:', error);
    }
  };

  const unarchiveNotification = async (id: string) => {
    try {
      await NotificationService.unarchive(id);
      // Real-time listener will update state automatically
    } catch (error) {
      console.error('Error unarchiving notification:', error);
    }
  };

  const updatePreferences = async (
    updates: Partial<NotificationPreferences>
  ) => {
    if (!user) return;

    try {
      await NotificationService.updatePreferences(user.uid, updates);
      await loadPreferences();
    } catch (error) {
      console.error('Error updating preferences:', error);
      throw error;
    }
  };

  const setEnabled = async (enabled: boolean) => {
    if (!user) return;

    try {
      await NotificationService.setEnabled(user.uid, enabled);
      await loadPreferences();
    } catch (error) {
      console.error('Error setting notification enabled status:', error);
      throw error;
    }
  };

  const pauseNotifications = async (hours: number) => {
    if (!user) return;

    try {
      await NotificationService.pauseNotifications(user.uid, hours);
      await loadPreferences();
    } catch (error) {
      console.error('Error pausing notifications:', error);
      throw error;
    }
  };

  // ==================== CONTEXT VALUE ====================

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    preferences,
    refresh,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    deleteNotification,
    archiveNotification,
    unarchiveNotification,
    updatePreferences,
    setEnabled,
    pauseNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// ==================== HOOK ====================

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error(
      'useNotifications must be used within a NotificationProvider'
    );
  }
  return context;
};
